import React, { useState } from "react";

import { isMessageKey } from "common/i18n/types";

import { Layout } from "../components/layout/layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { useAuth } from "../contexts/auth-context";
import { useTranslations } from "../hooks/use-translations";

export const ProfilePage = (): React.JSX.Element => {
  const { user, updateProfile } = useAuth();
  const { translations } = useTranslations();
  const [firstName, setFirstName] = useState(user?.firstName ?? ``);
  const [lastName, setLastName] = useState(user?.lastName ?? ``);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(``);
  const [success, setSuccess] = useState(``);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(``);
    setSuccess(``);

    try {
      await updateProfile(firstName, lastName);
      setSuccess(translations.profile.updated);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : translations.profile.failure;

      if (errorMessage === `unexpectedError` || errorMessage.includes(`Internal server error`)) {
        setError(translations.common.serverError);
      } else if (errorMessage === `networkError` || errorMessage.includes(`Network error`)) {
        setError(translations.common.networkError);
      } else if (isMessageKey(errorMessage)) {
        setError(translations.auth[errorMessage]);
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto space-y-6" data-testid="profile">
        <Card>
          <CardHeader>
            <CardTitle>{translations.profile.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleSubmit}>
              {error ? (
                <div
                  className="text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-md"
                  data-testid="profile-error"
                >
                  {error}
                </div>
              ) : null}

              {success ? (
                <div
                  className="text-green-600 dark:text-green-400 text-sm bg-green-50 dark:bg-green-900/20 p-3 rounded-md"
                  data-testid="profile-success"
                >
                  {success}
                </div>
              ) : null}

              <div>
                <Input
                  required
                  data-testid="first-name-input"
                  disabled={isLoading}
                  id="firstName"
                  name="firstName"
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder={translations.common.firstNamePlaceholder}
                  type="text"
                  value={firstName}
                />
              </div>

              <div>
                <Input
                  required
                  data-testid="last-name-input"
                  disabled={isLoading}
                  id="lastName"
                  name="lastName"
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder={translations.common.lastNamePlaceholder}
                  type="text"
                  value={lastName}
                />
              </div>

              <Button
                className="w-full"
                data-testid="profile-save-button"
                disabled={isLoading}
                isLoading={isLoading}
                loadingText={translations.profile.saving}
                type="submit"
              >
                {translations.profile.save}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
